import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { MapPin, Briefcase, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  useDocumentMeta({
    title: 'Careers | Revs Vibes',
    description: 'Explore career opportunities at Revs Vibes in automotive advisory, editorial, and collector car culture.',
    canonical: 'https://www.revsvibes.com/careers',
  });
  const { language } = useLanguage();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/careers`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setPositions(data))
      .catch(() => setPositions([]))
      .finally(() => setLoading(false));
  }, []);

  const t = {
    en: { title: 'CAREERS', subtitle: 'Join our team of passionate automotive professionals', apply: 'Apply Now', contact: 'Contact Us', noPositions: 'No open positions at this time.' },
    es: { title: 'CARRERAS', subtitle: 'Únete a nuestro equipo de profesionales automotrices apasionados', apply: 'Aplicar Ahora', contact: 'Contáctanos', noPositions: 'No hay posiciones abiertas en este momento.' },
    pt: { title: 'CARREIRAS', subtitle: 'Junte-se à nossa equipe de profissionais automotivos apaixonados', apply: 'Candidatar-se', contact: 'Contate-nos', noPositions: 'Nenhuma vaga aberta no momento.' },
    fr: { title: 'CARRIÈRES', subtitle: 'Rejoignez notre équipe de professionnels automobiles passionnés', apply: 'Postuler', contact: 'Contactez-nous', noPositions: 'Aucun poste ouvert pour le moment.' },
    it: { title: 'CARRIERE', subtitle: 'Unisciti al nostro team di professionisti automobilistici appassionati', apply: 'Candidati Ora', contact: 'Contattaci', noPositions: 'Nessuna posizione aperta al momento.' },
  };
  const tr = t[language] || t.en;

  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-96 bg-black text-white overflow-hidden clickable-card">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600" alt="Careers" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="careers-title">{tr.title}</h1>
            <p className="text-xl text-gray-300">{tr.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={32} /></div>
          ) : positions.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400 mb-8">{tr.noPositions}</p>
              <Link to="/contact"><Button className="bg-black hover:bg-gray-800 text-white">{tr.contact}</Button></Link>
            </div>
          ) : (
            <div className="space-y-6">
              {positions.map(pos => (
                <Card key={pos.id} className="clickable-card border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`career-card-${pos.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-black mb-2">{pos.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1"><MapPin size={14} /><span>{pos.location}</span></div>
                          <div className="flex items-center gap-1"><Briefcase size={14} /><span>{pos.type}</span></div>
                        </div>
                        {pos.description && <p className="text-gray-600 text-sm mt-3 line-clamp-2">{pos.description}</p>}
                        {pos.requirements && pos.requirements.length > 0 && (
                          <ul className="mt-3 space-y-1">
                            {pos.requirements.map((req, i) => (
                              <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                                {req}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <Link to="/contact">
                        <Button className="bg-black hover:bg-gray-800 text-white whitespace-nowrap" data-testid={`career-apply-${pos.id}`}>{tr.apply}</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Careers;
